import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';

import { TranslateModule } from '@ngx-translate/core';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { EllipsisDirective } from 'ngx-ellipsis';
import { firstValueFrom } from 'rxjs';

import { FormModalComponent } from '../../../modals/form-modal/form-modal.component';
import {
  Case_Overview,
  Patient_Details,
} from '../../../models/view-content.models/view-content.model';
import { DynamicListComponent } from '../../data-interaction/dynamic-list/dynamic-list.component';
import { FormioRendererI18n } from '../../data-interaction/formio-renderer/formio-renderer.component';

dayjs.extend(customParseFormat);

export interface DialogData {
  dateAndTime: string;
  catalog: string;
  content: string;
}

@Component({
  selector: 'app-patient-overview',
  templateUrl: './patient-overview.component.html',
  styleUrls: ['./patient-overview.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DynamicListComponent,
    EllipsisDirective,
    MatButtonModule,
    MatExpansionModule,
    MatGridListModule,
    MatIconModule,
    MatListModule,
    TranslateModule,
  ],
})
export class PatientOverviewComponent implements OnInit {
  public constructor(public dialog: MatDialog) {}
  protected readonly Object = Object;

  @Output() public changedOverviewData: EventEmitter<any> = new EventEmitter();
  @Input() patientDetailsI18n: FormioRendererI18n | undefined;
  @Input() public caseOverview: Case_Overview | undefined;
  @Input() vcPatientDetails: Patient_Details | undefined;

  public ngOnInit() {
    // setTimeout(() => {
    //   console.log('-----CASE OVERVIEW DATA-----');
    //   console.log('Medical History:', this.caseOverview?.medical_history);
    //   console.log('Diagnosis:', this.caseOverview?.diagnose);
    //   console.log('Visit Record:', this.caseOverview?.visit_record);
    //   console.log('Discharge:', this.caseOverview?.discharge);
    //   console.log('Consultation:', this.caseOverview?.consultation);
    //   console.log('Therapy:', this.caseOverview?.therapy);
    //   console.log('Task:', this.caseOverview?.task);
    //   console.log('Diagnostic:', this.caseOverview?.diagnostic);
    //   console.log('Pathogen:', this.caseOverview?.pathogen);
    //   console.log('Post Treatment:', this.caseOverview?.post_treatment);
    //   console.log('---------------------------');
    // }, 1000);

    if (this.caseOverview?.consultation) {
      this.caseOverview.consultation = this.showGoalDateOn(this.caseOverview.consultation);
    }

    if (this.caseOverview?.therapy) {
      this.caseOverview.therapy = this.showGoalDateOn(this.caseOverview.therapy);
    }

    if (this.caseOverview?.task) {
      this.caseOverview.task = this.showGoalDateOn(this.caseOverview.task);
    }

    if (this.caseOverview?.diagnostic) {
      this.caseOverview.diagnostic = this.showGoalDateOn(this.caseOverview.diagnostic);
    }
  }

  public async addNewVc(
    formFileName: string,
    type: string,
    vcPatientDetails?: Patient_Details,
  ): Promise<void> {
    let initialData = null;

    if (type === 'therapy') {
      initialData = {
        data: {
          editor: {
            doctors: false,
            nurse: false,
            tx: true,
          },
        },
      };
    }

    const dialogRef = this.dialog.open(FormModalComponent, {
      data: {
        form_file_name: formFileName,
        form_data: initialData,
        vcPatientDetails: vcPatientDetails,
        hideCurrentPlace: true,
      },
      panelClass: 'patient-overview-dialog-container',
    });

    const res = await firstValueFrom(dialogRef.afterClosed());

    if (!res || res.role !== 'save') return;

    if (type === 'therapy' && res.data) {
      if (Array.isArray(res.data)) {
        res.data = res.data.map((item: any) => {
          if (item && item.editor) {
            item.editor = {
              doctors: false,
              nurse: false,
              tx: true,
            };
          }
          return item;
        });
      }
    }

    this.changedOverviewData.emit({
      type: type,
      method: 'ADD',
      data: res.data,
    });
  }
  //#endregion

  public async updateVc(
    data: any,
    formFileName: string,
    type: string,
    vc?: any,
    vcPatientDetails?: Patient_Details,
    viewContentI18n?: FormioRendererI18n,
    history?: any[],
  ): Promise<void> {
    const formData = { data: JSON.parse(JSON.stringify(data || {})) };

    if (type === 'therapy') {
      if (formData.data && formData.data.editor) {
        formData.data.editor = {
          doctors: false,
          nurse: false,
          tx: true,
        };
      }
    }

    if (type === 'task' && formData.data.goalDateOn) {
      formData.data.goalDateOn = this.getFormattedDate(formData.data.goalDateOn);
    }

    const dialogRef = this.dialog.open(FormModalComponent, {
      data: {
        form_data: formData,
        form_file_name: formFileName,
        history,
        viewContentI18n,
        vcPatientDetails: vcPatientDetails,
        hideCurrentPlace: true,
      },
      maxHeight: 'calc(100vh - 16px)',
      panelClass: 'patient-overview-dialog-container',
    });

    const res = await firstValueFrom(dialogRef.afterClosed());

    if (!res || res.role === 'cancel') return;

    if (type === 'therapy' && res.data) {
      if (Array.isArray(res.data)) {
        res.data = res.data.map((item: any) => {
          if (item && item.editor) {
            item.editor = {
              doctors: false,
              nurse: false,
              tx: true,
            };
          }
          return item;
        });
      }
    }

    this.changedOverviewData.emit({
      type: type,
      method: 'UPDATE',
      data: res.data,
      vc: vc,
    });
  }

  private showGoalDateOn(data: any[]): any[] {
    return data.map(el => {
      const updatedEl = JSON.parse(JSON.stringify(el));

      if (updatedEl.data && updatedEl.data.goalDateOn) {
        updatedEl.data.goalDateOn = this.getFormattedDate(updatedEl.data.goalDateOn);
        updatedEl.data.goal_date = 'on';
      }

      if (updatedEl.data && updatedEl.data.dayCountDeactivateOn) {
        updatedEl.data.dayCountDeactivate = 'on';
      }

      return updatedEl;
    });
  }

  public getFormattedDate(date: string | Date): string {
    if (!date) return '';

    return dayjs(date).format('YYYY-MM-DDTHH:mm');
  }
}
